/**
 * Put a removed member back, with the shares.
 *
 * The case this exists for is the one where nobody inside the group can help:
 * an account is taken over and the intruder removes its owner. Normally
 * re-admission is an invite from a member — here the members are the attacker.
 *
 * It takes a role for the same reason. An intruder who demoted somebody before
 * removing them would otherwise be undone only halfway, leaving the real owner
 * back in their own group as a member while the attacker still holds it.
 *
 * That role is exactly why this needs the recovery shares. Ownership is not
 * about reading photographs — a wrong key leaves somebody locked out of every
 * image — it is about control: an owner can remove every member and delete the
 * group, none of which needs a key that works. An administrator able to appoint
 * owners at will could destroy any group here without decrypting anything.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { errorMessage, trpc, trpcClient } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { useToast } from "../../components/ui/Toast.js";
import {
  ChallengeFields,
  challengeReady,
  type ChallengeState,
} from "./ChallengeFields.js";

type Role = "owner" | "admin" | "member";

export function ReadmitMember({
  groupId,
  userId,
  name,
  previousRole,
}: {
  groupId: string;
  userId: string;
  name: string;
  /** What they held before they were removed, offered as the default. */
  previousRole: Role;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<Role>(previousRole);
  const [reason, setReason] = useState("");
  const [challenge, setChallenge] = useState<ChallengeState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const readmit = useMutation({
    mutationFn: () =>
      trpcClient.admin.readmitMember.mutate({
        groupId,
        userId,
        role,
        reason: reason.trim(),
        challengeToken: challenge?.token ?? "",
        challengeAnswer: challenge?.answer.trim() ?? "",
      }),
    onSuccess: async (result) => {
      setOpen(false);
      setChallenge(null);
      setReason("");
      setError(null);

      /*
       * Says whether the job is finished. Being in a group is not the same as
       * being able to read it — the key grant is separate, and is what the
       * regrant upload restores.
       */
      toast(
        result.hasKey
          ? `${name} is back, and already holds the group's key.`
          : `${name} is back. They still need a regrant to open anything.`,
      );

      await queryClient.invalidateQueries({
        queryKey: trpc.admin.groupMembers.queryKey({ groupId }),
      });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  if (!open) {
    return (
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Re-admit
      </Button>
    );
  }

  return (
    <form
      className="fc-security__upload"
      onSubmit={(event) => {
        event.preventDefault();
        readmit.mutate();
      }}
    >
      <p className="fc-admin__hint">Put {name} back into this group.</p>

      <Field label="As" htmlFor={`fc-role-${userId}`}>
        <select
          id={`fc-role-${userId}`}
          className="fc-share__select"
          value={role}
          onChange={(event) => setRole(event.target.value as Role)}
        >
          <option value="member">Member</option>
          <option value="admin">Admin</option>
          <option value="owner">Owner</option>
        </select>
      </Field>

      <ChallengeFields id={userId} state={challenge} onChange={setChallenge} />

      <Field label="Why" htmlFor={`fc-why-${userId}`} required>
        <Input
          id={`fc-why-${userId}`}
          value={reason}
          maxLength={500}
          placeholder="Support request #14 — account taken over"
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>

      {error && (
        <p className="fc-admin__error" role="alert">
          {error}
        </p>
      )}

      <div className="fc-security__export">
        <Button
          type="submit"
          variant="primary"
          loading={readmit.isPending}
          disabled={reason.trim().length < 3 || !challengeReady(challenge)}
        >
          Re-admit
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
